import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useLabelStore } from '@/stores/labelStore'
import { useElementsStore } from '@/stores/elementsStore'
import { parseZPL, isValidZPL } from '@/lib/zpl/parser'
import { DPI } from '@/lib/utils/units'
import { Upload, AlertCircle, FileCode } from 'lucide-react'

interface ImportZPLDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

// Common label sizes in mm
const LABEL_PRESETS = [
  { name: '4x6 in (shipping)', width: 101.6, height: 152.4 },
  { name: '4x4 in', width: 101.6, height: 101.6 },
  { name: '2x1 in', width: 50.8, height: 25.4 },
  { name: '100x100 mm', width: 100, height: 100 },
  { name: '100x150 mm', width: 100, height: 150 },
  { name: '50x30 mm', width: 50, height: 30 },
]

export function ImportZPLDialog({ open, onOpenChange }: ImportZPLDialogProps) {
  const { label, setLabel } = useLabelStore()
  const { setElements } = useElementsStore()

  const [zplCode, setZplCode] = useState('')
  const [dpi, setDpi] = useState<DPI>(203)
  const [labelWidth, setLabelWidth] = useState<number>(100)
  const [labelHeight, setLabelHeight] = useState<number>(150)
  const [error, setError] = useState<string | null>(null)
  const [warnings, setWarnings] = useState<string[]>([])
  const [previewInfo, setPreviewInfo] = useState<{
    elementsCount: number
    width?: number
    height?: number
    autoWidth?: number
    autoHeight?: number
  } | null>(null)

  const handleZPLChange = (value: string) => {
    setZplCode(value)
    setError(null)
    setWarnings([])
    setPreviewInfo(null)

    if (value.trim()) {
      if (!isValidZPL(value)) {
        setError('Invalid ZPL code. Must contain ^XA and ^XZ markers.')
        return
      }

      try {
        const result = parseZPL(value, dpi)

        // Calculate auto-size based on element positions
        let maxX = 0
        let maxY = 0
        for (const el of result.elements) {
          const elRight = el.position.x + ('width' in el ? (el.width || 0) : 0) + 10
          const elBottom = el.position.y + ('height' in el ? (el.height || 0) : 0) + 10
          maxX = Math.max(maxX, elRight)
          maxY = Math.max(maxY, elBottom)
        }

        setPreviewInfo({
          elementsCount: result.elements.length,
          width: result.label.width,
          height: result.label.height,
          autoWidth: maxX > 0 ? Math.ceil(maxX / 10) * 10 : undefined,
          autoHeight: maxY > 0 ? Math.ceil(maxY / 10) * 10 : undefined,
        })
        setWarnings(result.warnings)

        // Auto-set label size if found in ZPL
        if (result.label.width) setLabelWidth(Math.round(result.label.width))
        if (result.label.height) setLabelHeight(Math.round(result.label.height))
      } catch (e) {
        setError('Failed to parse ZPL code.')
      }
    }
  }

  const handleDPIChange = (value: string) => {
    const newDpi = parseInt(value) as DPI
    setDpi(newDpi)
    // Re-parse with new DPI
    if (zplCode.trim() && isValidZPL(zplCode)) {
      handleZPLChange(zplCode)
    }
  }

  const handlePresetChange = (value: string) => {
    const preset = LABEL_PRESETS.find(p => p.name === value)
    if (preset) {
      setLabelWidth(preset.width)
      setLabelHeight(preset.height)
    }
  }

  const handleImport = () => {
    if (!isValidZPL(zplCode)) {
      setError('Invalid ZPL code. Must contain ^XA and ^XZ markers.')
      return
    }

    try {
      const result = parseZPL(zplCode, dpi)

      // Use detected size, manual size, or auto-calculated size
      const finalWidth = result.label.width || labelWidth
      const finalHeight = result.label.height || labelHeight

      setLabel({
        ...label,
        dpi,
        width: finalWidth,
        height: finalHeight,
      })

      // Set elements (this will add to history)
      setElements(result.elements)

      onOpenChange(false)
      setZplCode('')
      setPreviewInfo(null)
      setWarnings([])
    } catch (e) {
      setError('Failed to import ZPL code.')
    }
  }

  const handleFileImport = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.zpl,.txt'
    input.onchange = (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (!file) return

      const reader = new FileReader()
      reader.onload = (event) => {
        const content = event.target?.result as string
        handleZPLChange(content)
      }
      reader.readAsText(file)
    }
    input.click()
  }

  const handleClose = () => {
    onOpenChange(false)
    setZplCode('')
    setError(null)
    setWarnings([])
    setPreviewInfo(null)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCode className="h-5 w-5" />
            Import ZPL Code
          </DialogTitle>
          <DialogDescription>
            Paste ZPL code or import from a file to render it in the designer.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="dpi">Printer DPI</Label>
              <Select value={dpi.toString()} onValueChange={handleDPIChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="203">203 DPI</SelectItem>
                  <SelectItem value="300">300 DPI</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Label Size Preset</Label>
              <Select onValueChange={handlePresetChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select preset..." />
                </SelectTrigger>
                <SelectContent>
                  {LABEL_PRESETS.map((preset) => (
                    <SelectItem key={preset.name} value={preset.name}>
                      {preset.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="width">Width (mm)</Label>
              <Input
                id="width"
                type="number"
                value={labelWidth}
                onChange={(e) => setLabelWidth(parseFloat(e.target.value) || 100)}
                min={10}
                max={500}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="height">Height (mm)</Label>
              <Input
                id="height"
                type="number"
                value={labelHeight}
                onChange={(e) => setLabelHeight(parseFloat(e.target.value) || 100)}
                min={10}
                max={500}
              />
            </div>
          </div>

          <div className="grid gap-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="zpl-code">ZPL Code</Label>
              <Button variant="outline" size="sm" onClick={handleFileImport}>
                <Upload className="h-4 w-4 mr-2" />
                Import File
              </Button>
            </div>
            <Textarea
              id="zpl-code"
              placeholder="^XA&#10;^FO50,50^A0N,30,30^FDHello World^FS&#10;^XZ"
              value={zplCode}
              onChange={(e) => handleZPLChange(e.target.value)}
              className="font-mono text-sm h-40"
            />
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {warnings.length > 0 && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="text-sm">
                  <strong>Warnings:</strong>
                  <ul className="list-disc list-inside mt-1">
                    {warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {previewInfo && !error && (
            <div className="rounded-md bg-muted p-3 text-sm">
              <div className="font-medium mb-1">Preview:</div>
              <div className="text-muted-foreground space-y-1">
                <div>{previewInfo.elementsCount} element(s) detected</div>
                {previewInfo.width && previewInfo.height && (
                  <div>
                    Label size from ZPL: {previewInfo.width.toFixed(1)}x{previewInfo.height.toFixed(1)}mm
                  </div>
                )}
                {!previewInfo.width && !previewInfo.height && previewInfo.autoWidth && previewInfo.autoHeight && (
                  <div className="text-yellow-600">
                    No size in ZPL. Suggested: {previewInfo.autoWidth}x{previewInfo.autoHeight}mm (based on content)
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={!zplCode.trim() || !!error}
          >
            Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
